class LimitedAddToggle extends HTMLElement {
  constructor() {
    super();
    this.onChangeHandler = this.debounce((event) => this.onChange(event), 300);
    this.onPageShow = (event) => {
      if (event.persisted) this.syncWithServer();
    };
    this.onVisibilityChange = () => {
      if (document.visibilityState === "visible") this.syncWithServer();
    };
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="checkbox"]');
    this.label = this.querySelector("label");

    this.addEventListener("change", this.onChangeHandler);
    window.addEventListener("pageshow", this.onPageShow);
    document.addEventListener("visibilitychange", this.onVisibilityChange);

    if (this.quantityInCart > this.maxInCart) this.heal();
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.onChangeHandler);
    window.removeEventListener("pageshow", this.onPageShow);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  get variantId() {
    return Number(this.dataset.variantId);
  }

  get maxInCart() {
    const value = parseInt(this.dataset.maxInCart, 10);
    return Number.isFinite(value) && value >= 0 ? value : 1;
  }

  get addQuantity() {
    const value = parseInt(this.dataset.addQuantity, 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  get mode() {
    return this.dataset.mode === "deferred" ? "deferred" : "immediate";
  }

  get properties() {
    if (!this.dataset.properties) return undefined;
    try {
      return JSON.parse(this.dataset.properties);
    } catch (e) {
      return undefined;
    }
  }

  get quantityInCart() {
    return parseInt(this.dataset.quantityInCart, 10) || 0;
  }

  set quantityInCart(value) {
    this.dataset.quantityInCart = String(value);
  }

  sectionsToRender() {
    if (this.dataset.sections) {
      return this.dataset.sections.split(",").map((s) => s.trim()).filter(Boolean);
    }
    if (this.mode !== "immediate") return undefined;
    const section = this.closest("[data-section-id]");
    if (!section) return undefined;
    const header = document.querySelector(".jsHeader [data-section-id]");
    return [section.dataset.sectionId, header && header.dataset.sectionId].filter(Boolean);
  }

  onChange(event) {
    const checked = event.target.checked;

    if (checked && this.quantityInCart === 0) {
      if (this.mode === "deferred") return; 
      this.runTask(() => this.add());
      return;
    }

    if (!checked && this.quantityInCart > 0) {
      this.runTask(() => this.remove());
    }
  }

  commit() {
    // скрытый тумблер (ваза на неподходящем размере, MFS-5794) корзину не меняет
    if (!this.input || this.hidden) return Promise.resolve();
    if (this.input.checked && this.quantityInCart === 0) return this.runTask(() => this.add());
    if (!this.input.checked && this.quantityInCart > 0) return this.runTask(() => this.remove());
    return Promise.resolve();
  }

  // Для формы товара: строка для общего /cart/add.js вместе с букетом, вместо
  // отдельного commit() с собственным /cart.js. Лимит — по снимку корзины,
  // который отдаёт форма (MFS-5356: устаревшая страница не кладёт вторую вазу).
  deferredItem(cart) {
    if (this.mode !== "deferred" || this.hidden || !this.input || !this.input.checked) return null;

    let have = this.quantityInCart;
    if (cart && Array.isArray(cart.items)) {
      have = cart.items
        .filter((item) => Number(item.id) === this.variantId)
        .reduce((sum, item) => sum + (item.quantity || 0), 0);
      this.quantityInCart = have;
    }
    if (have >= this.maxInCart) return null;

    return {
      variantId: this.variantId,
      quantity: Math.min(this.addQuantity, this.maxInCart - have),
      properties: this.properties,
    };
  }

  applyAdded(item) {
    this.quantityInCart = this.quantityInCart + ((item && item.quantity) || 0);
    if (this.input && this.quantityInCart > 0) this.input.checked = true;
  }

  async add() {
    const result = await window.CartService.addItem({
      variantId: this.variantId,
      quantity: this.addQuantity,
      properties: this.properties,
      maxInCart: this.maxInCart,
      sections: this.sectionsToRender(),
    });

    this.applyResult(result);

    if (result.status === "at-limit") {
      this.emit("limited-add:limit", { quantityInCart: result.quantityInCart, maxInCart: this.maxInCart });
    } else {
      this.emit("limited-add:changed", { status: result.status, quantityInCart: result.quantityInCart });
    }
    return result;
  }

  async remove() {
    const result = await window.CartService.removeVariant({
      variantId: this.variantId,
      sections: this.sectionsToRender(),
    });
    this.applyResult(result);
    this.emit("limited-add:changed", { status: "removed", quantityInCart: result.quantityInCart });
    return result;
  }

  syncWithServer() {
    const now = Date.now();
    if (this.lastSyncAt && now - this.lastSyncAt < 3000) return Promise.resolve();
    this.lastSyncAt = now;

    return window.CartService.getVariantQuantity(this.variantId, true)
      .then((quantity) => {
        const changed = quantity !== this.quantityInCart;
        this.quantityInCart = quantity;

        if (quantity > 0 && this.input) this.input.checked = true;
        if (quantity === 0 && this.input && this.mode === "immediate") this.input.checked = false;

        if (quantity > this.maxInCart) return this.heal();
        if (changed && this.mode === "immediate") return this.refreshSectionHtml();
        return undefined;
      })
      .catch(() => {});
  }

  heal() {
    return this.runTask(() =>
      window.CartService.enforceLimit({
        variantId: this.variantId,
        maxInCart: this.maxInCart,
        sections: this.sectionsToRender(),
      }).then((result) => {
        this.applyResult(result);
        return result;
      })
    );
  }

  runTask(taskFn) {
    this.setLoading(true);
    return Promise.resolve()
      .then(taskFn)
      .catch((error) => {
        console.error("[limited-add-toggle]", error);
        this.emit("limited-add:error", { error });
        this.lastSyncAt = 0; 
        return this.syncWithServer();
      })
      .finally(() => this.setLoading(false));
  }

  applyResult(result) {
    if (!result) return;
    this.quantityInCart = result.quantityInCart;
    if (this.input) {
      this.input.checked = result.quantityInCart > 0 || (this.mode === "deferred" && this.input.checked);
    }
    // На странице корзины после нас началось другое изменение — его ответ свежее.
    // Наш рендер пропускаем, но более поздний токен может и не прислать секций
    // (intent без запроса), поэтому просим jsCart свериться с сервером на settle.
    if (window.PXUTheme?.jsCart?.isServiceResponseStale?.()) {
      window.PXUTheme.jsCart.forceReconcile = true;
      return;
    }
    if (result.data && result.data.sections) this.renderSections(result.data.sections);
    this.refreshGlobalCart(result.data);
  }

  setLoading(enabled) {
    this.toggleAttribute("aria-busy", enabled);
    if (!this.label) return;
    if (enabled) this.label.setAttribute("disabled", "disabled");
    else this.label.removeAttribute("disabled");
  }

  emit(name, detail) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  }

  renderSections(sections) {
    const target = this.closest("[data-section-id]");
    if (!target) return;
    const html = sections[target.dataset.sectionId];
    if (!html) return;
    const source = new DOMParser().parseFromString(html, "text/html").querySelector(".section");
    if (source) target.innerHTML = source.innerHTML;
  }

  refreshSectionHtml() {
    const target = this.closest("[data-section-id]");
    if (!target) return Promise.resolve();
    return fetch(`${window.location.pathname}?sections=${target.dataset.sectionId}`)
      .then((res) => res.json())
      .then((sections) => this.renderSections(sections))
      .catch(() => {});
  }

  refreshGlobalCart(data) {
    try {
      if (
        data && data.sections &&
        window.PXUTheme && window.PXUTheme.jsHeader && window.PXUTheme.jsHeader.updateCartItemsCount
      ) {
        window.PXUTheme.jsHeader.updateCartItemsCount(data.sections);
      }
    } catch (e) { /* ignore */ }
  }
}

if (!customElements.get("limited-add-toggle")) {
  customElements.define("limited-add-toggle", LimitedAddToggle);
}
